import {useState} from "react";
import NotificationManager from "../../components/notifications/NotificationManager";
import WatsonApi from "../../backends/WatsonApi";
import {useStores} from "../../stores/useStores";
import styles from "./styles/AnalyticsSettings.module.scss";


export function AnalyticsSettings(props) {
    const {organizationStore, i18nStore} = useStores();
    const [hasChanged, setHasChanged] = useState(false);
    const initialId = organizationStore.organization.json_data.settings.analytics?.google_analytics_id || "";
    const [google_analytics_id, setGoogleAnalyticsId] = useState(initialId.startsWith('G-') ? initialId.substring(2) : initialId);
    const [validationMessage, setValidationMessage] = useState("");
    const [validationError, setValidationError] = useState(false);

    function extractGAId(text: string): string | null {
        const patterns = [
            /G-[A-Z0-9]{10}/i,  // Match G-XXXXXXXXXX pattern
            /['"]G-[A-Z0-9]{10}['"]/i,  // Match quoted version
        ];

        for (const pattern of patterns) {
            const match = text.match(pattern);
            if (match) {
                // Remove quotes and G- prefix
                let id = match[0].replace(/['"]/g, '');
                return id.startsWith('G-') ? id.substring(2) : id;
            }
        }
        return null;
    }

    function validateGAId(id: string): boolean {
        if (!id) return true; // Empty is valid (optional field)
        return /^[A-Z0-9]{10}$/i.test(id);
    }

    function handleGAIdChange(value: string): void {
        let finalValue = value;
        let message = "";
        let isError = false;

        // If user pastes G- at the beginning, remove it
        if (finalValue.startsWith('G-')) {
            finalValue = finalValue.substring(2);
        }

        // Check if user pasted a script (contains < or script tag)
        if (value.includes('<') || value.includes('script') || value.length > 50) {
            const extractedId = extractGAId(value);
            if (extractedId) {
                finalValue = extractedId;
                message = `Extracted GA ID "G-${extractedId}" from script.`;
            } else {
                message = "Could not extract a valid Google Analytics ID from the pasted content.";
                isError = true;
            }
        }

        if (finalValue && !validateGAId(finalValue)) {
            message = "Google Analytics ID should be 10 alphanumeric characters (G- prefix is added automatically)";
            isError = true;
        }

        setGoogleAnalyticsId(finalValue);
        setValidationMessage(message);
        setValidationError(isError);
        setHasChanged(true);
    }

    function handlePaste(e: React.ClipboardEvent<HTMLInputElement>): void {
        const pastedText = e.clipboardData.getData('text');

        if (pastedText.includes('<') || pastedText.includes('script') || pastedText.length > 50 || pastedText.startsWith('G-')) {
            e.preventDefault();
            handleGAIdChange(pastedText);
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (validationError) {
            NotificationManager.error("Please fix validation errors before saving.");
            return;
        }

        try {
            const client = await WatsonApi();
            const fullGAId = google_analytics_id ? `G-${google_analytics_id}` : null;
            const response = await client.apis.settings.settings_update({
                "id": organizationStore.currentOrganization.id,
                "data": {
                    "analytics": {
                        "google_analytics_id": fullGAId,
                    }
                }
            });

            const settings = response.body.settings;
            if (settings) {
                organizationStore.organization.json_data.settings = settings;
                NotificationManager.success("Settings successfully updated!");
                setValidationMessage("");
                setHasChanged(false);
            }
        } catch (e) {
            switch (e.statusCode) {
                case 400:
                    NotificationManager.error("Bad Request: Settings not updated.");
                    break;
                case 404:
                    NotificationManager.error("Not Found: Organization settings not found.");
                    break;
                case 500:
                    NotificationManager.error("Internal Server Error: Settings not updated.");
                    break;
                default:
                    NotificationManager.error("Unknown Error: Settings not updated.");
            }
        }
    }

    return <div role="tabpanel">
        <div className="sb-settings-block-content-heading">
            <h4>Analytics Settings</h4>
        </div>
        <form id="analytics-form"
              onSubmit={handleSubmit}
              onChange={() => setHasChanged(true)}
        >
            <div className="row">
                <div className="col-sm-3">
                    <label>Google Analytics ID</label>
                </div>
                <div className="col-sm-4">
                    <div className={styles.inputWrapper}>
                        <span className={styles.inputPrefix}>
                            G-
                        </span>
                        <input
                            title="Google Analytics Id"
                            placeholder="XXXXXXXXXX"
                            onChange={e => handleGAIdChange(e.target.value)}
                            onPaste={handlePaste}
                            value={google_analytics_id}
                            className={styles.input}
                            maxLength={10}
                        />
                    </div>
                    {validationMessage && (
                        <div className={`${styles.validationMessage} ${validationError ? styles.error : styles.info}`}>
                            {validationMessage}
                        </div>
                    )}
                </div>
            </div>
            <div className="sb-settings-block-footer">
                <button type="submit" className="btn sb-organization-color-element-bg" name="submit"
                        disabled={!hasChanged}>Save
                </button>
            </div>
        </form>
    </div>
}
